// @flow

import classnames from 'classnames';
import React, { type Node, useContext, useState } from 'react';

import { Scrollable } from 'Components/frameworks/scrollable/Scrollable';
import { Slider } from 'Components/frameworks/slider/Slider.jsx';
import { Tag } from 'Components/frameworks/tag/Tag';
import {
  type DestinationGroup,
  TOP_DESTINATIONS_AREA_ID,
} from 'Components/lines/destinations-line/DestinationsLine.utils';
import { COLORS } from 'Components/utils/color/colors';
import ResponsiveContext from 'Components/utils/responsive/responsiveContext';
import { t } from 'Src/i18n';
import { type TrackingPayload } from 'Tracking/tracking-api/useTracking';
import { DestinationCard } from './DestinationCard';

type Props = {
  destinations: Array<DestinationGroup>,
  header?: Node | null,
  productType: string,
  trackingData: TrackingPayload,
};

export const Destinations = ({ destinations, header, productType, trackingData }: Props) => {
  // use isDesktop to display slider's controls only on desktops
  const { isTablet, isDesktop } = useContext(ResponsiveContext);
  const [selectedDestinationIndex, setSelectedDestinationIndex] = useState(0);
  const {
    areaId: selectedAreaId,
    areaLabel: selectedAreaLabel,
    destinations: selectedAreaDestinations,
  } = destinations[selectedDestinationIndex];
  return (
    <div className="flex flex-wrap w-full max-w-site">
      <Scrollable
        childrenWrapperClassNames="flex"
        className="w-screen lg:w-full -mx-5 lg:mx-0 mb-4"
        header={header}
        navPosition="top"
      >
        {destinations.map((area, index) => (
          <Tag
            active={index === selectedDestinationIndex}
            borderColor={index === selectedDestinationIndex ? COLORS.BLUE : COLORS.GRAY_LIGHT}
            className={classnames('rtl:mr-2 mb-2', { 'rtl:ml-5 lg:rtl:ml-0': index === 0 })}
            color={index === selectedDestinationIndex ? COLORS.BLUE : COLORS.GRAY_DARKER}
            key={area.areaId}
            label={area.areaLabel}
            onClick={() => setSelectedDestinationIndex(index)}
            trackingData={{
              ...trackingData,
              labelGA:
                area.areaId === TOP_DESTINATIONS_AREA_ID
                  ? 'topDestinations_tab'
                  : `${area.areaLabel}_tab`,
            }}
            theme="outlinedThin"
          />
        ))}
      </Scrollable>
      <Slider
        className="w-screen lg:w-full -mx-5 lg:mx-0"
        cols={0}
        hasControls={isDesktop && selectedAreaDestinations.length > 3}
        nbItemsToScroll={isTablet ? 2 : 1}
        scrollBackwardLabel={t('common.scrollBackward')}
        scrollForwardLabel={t('common.scrollForward')}
        navPosition="center"
        scrollAreaContainerClassName="overflow-x-auto lg:overflow-hidden"
        scrollControlsClassName="-mx-5 lg:mx-0"
      >
        {selectedAreaDestinations.map((destination, index) => (
          <div
            key={destination.id}
            className={classnames('relative rtl:mr-5', {
              'rtl:ml-5 lg:rtl:ml-0': index === 0,
              'lg:rtl:mr-0': index === selectedAreaDestinations.length - 1,
            })}
            style={{ width: '55vw', maxWidth: '280px' }}
          >
            <DestinationCard
              {...destination}
              productType={productType}
              trackingData={{
                ...trackingData,
                labelGA:
                  selectedAreaId === TOP_DESTINATIONS_AREA_ID
                    ? `topDestinations_${destination.label}`
                    : `${selectedAreaLabel}_${destination.label}`,
              }}
            />
          </div>
        ))}
      </Slider>
    </div>
  );
};
